import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  Mail,
  Smartphone,
  AlertCircle,
  CheckCircle,
  Save,
  RefreshCw
} from 'lucide-react';
import type { RootState, AppDispatch } from '@/store';
import { updatePreferences } from '@/store/slices/notificationsSlice';
import type { NotificationPreferences } from '@/types';
import {
  GlassCard,
  GlassButton,
  GlassAlert,
  GlassPageHeader,
  SectionReveal,
} from '@/design-system';

const NotificationPreferencesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { preferences } = useSelector((state: RootState) => state.notifications);

  const [localPreferences, setLocalPreferences] = useState<NotificationPreferences>({
    emailNotifications: true,
    pushNotifications: true,
    epicStatusChanges: true,
    dependencyUpdates: true,
    capacityAlerts: true,
    readinessGateViolations: true,
    weeklyDigest: true,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load preferences when component mounts or preferences change
  useEffect(() => {
    if (preferences) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  const handleToggle = (key: keyof NotificationPreferences) => {
    setLocalPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      // In a real app, this would make an API call to save preferences
      // For now, we'll just update the Redux store
      dispatch(updatePreferences(localPreferences));

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save preferences:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setLocalPreferences({
      emailNotifications: true,
      pushNotifications: true,
      epicStatusChanges: true,
      dependencyUpdates: true,
      capacityAlerts: true,
      readinessGateViolations: true,
      weeklyDigest: true,
    });
  };

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <SectionReveal>
          <div className="flex items-center gap-4 mb-6">
            <Link
              to="/notifications"
              className="p-2 hover:bg-surface-glass-hover rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </Link>

            <GlassPageHeader
              title="Notification Preferences"
              subtitle="Customize how and when you receive notifications"
              icon={<Bell className="w-6 h-6" />}
              iconGradient="amber"
            />
          </div>
        </SectionReveal>

        {/* Save Success Message */}
        {saveSuccess && (
          <SectionReveal>
            <GlassAlert
              variant="success"
              message="Preferences saved successfully!"
            />
          </SectionReveal>
        )}

        {/* Delivery Methods */}
        <SectionReveal delay={0.1}>
          <GlassCard padding="lg">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Delivery Methods
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-surface-glass rounded-lg border border-border-subtle">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-accent-blue" />
                  <div>
                    <h3 className="text-sm font-medium text-foreground">Email Notifications</h3>
                    <p className="text-xs text-muted-foreground/60">
                      Receive notifications via email
                    </p>
                  </div>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localPreferences.emailNotifications}
                    onChange={() => handleToggle('emailNotifications')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-surface-glass peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-blue/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-blue"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-surface-glass rounded-lg border border-border-subtle">
                <div className="flex items-center gap-3">
                  <Smartphone className="w-5 h-5 text-accent-emerald" />
                  <div>
                    <h3 className="text-sm font-medium text-foreground">Push Notifications</h3>
                    <p className="text-xs text-muted-foreground/60">
                      Receive real-time browser notifications
                    </p>
                  </div>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localPreferences.pushNotifications}
                    onChange={() => handleToggle('pushNotifications')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-surface-glass peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-blue/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-blue"></div>
                </label>
              </div>
            </div>
          </GlassCard>
        </SectionReveal>

        {/* Notification Types */}
        <SectionReveal delay={0.15}>
          <GlassCard padding="lg">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Notification Types
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-surface-glass rounded-lg border border-border-subtle">
                <div>
                  <h3 className="text-sm font-medium text-foreground">EPIC Status Changes</h3>
                  <p className="text-xs text-muted-foreground/60">
                    Get notified when EPICs you own or contribute to change status
                  </p>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localPreferences.epicStatusChanges}
                    onChange={() => handleToggle('epicStatusChanges')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-surface-glass peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-blue/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-blue"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-surface-glass rounded-lg border border-border-subtle">
                <div>
                  <h3 className="text-sm font-medium text-foreground">Dependency Updates</h3>
                  <p className="text-xs text-muted-foreground/60">
                    Get notified about changes to dependencies that affect your work
                  </p>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localPreferences.dependencyUpdates}
                    onChange={() => handleToggle('dependencyUpdates')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-surface-glass peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-blue/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-blue"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-surface-glass rounded-lg border border-border-subtle">
                <div>
                  <h3 className="text-sm font-medium text-foreground">Capacity Alerts</h3>
                  <p className="text-xs text-muted-foreground/60">
                    Get notified about capacity over-allocations and compliance issues
                  </p>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localPreferences.capacityAlerts}
                    onChange={() => handleToggle('capacityAlerts')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-surface-glass peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-blue/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-blue"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-surface-glass rounded-lg border border-border-subtle">
                <div>
                  <h3 className="text-sm font-medium text-foreground">Readiness Gate Violations</h3>
                  <p className="text-xs text-muted-foreground/60">
                    Get notified when quarterly readiness gates are not met
                  </p>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localPreferences.readinessGateViolations}
                    onChange={() => handleToggle('readinessGateViolations')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-surface-glass peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-blue/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-blue"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-surface-glass rounded-lg border border-border-subtle">
                <div>
                  <h3 className="text-sm font-medium text-foreground">Weekly Digest</h3>
                  <p className="text-xs text-muted-foreground/60">
                    Receive a weekly summary of portfolio activities and updates
                  </p>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localPreferences.weeklyDigest}
                    onChange={() => handleToggle('weeklyDigest')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-surface-glass peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-blue/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-blue"></div>
                </label>
              </div>
            </div>
          </GlassCard>
        </SectionReveal>

        {/* Actions */}
        <SectionReveal delay={0.2}>
          <GlassCard padding="lg">
            <div className="flex items-center justify-between">
              <GlassButton
                onClick={handleReset}
                variant="secondary"
                leftIcon={<RefreshCw className="w-4 h-4" />}
              >
                Reset to Defaults
              </GlassButton>

              <GlassButton
                onClick={handleSave}
                disabled={isSaving}
                variant="primary"
                leftIcon={isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              >
                {isSaving ? 'Saving...' : 'Save Preferences'}
              </GlassButton>
            </div>
          </GlassCard>
        </SectionReveal>

        {/* Information */}
        <SectionReveal delay={0.25}>
          <GlassCard padding="lg" className="border-accent-blue/30 bg-accent-blue/5">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-accent-blue mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-accent-blue mb-1">
                  About Notifications
                </h3>
                <p className="text-xs text-muted-foreground">
                  Your notification preferences control how you receive updates about portfolio activities.
                  Email notifications are sent to your registered email address, while push notifications
                  appear in your browser when you&apos;re online. You can adjust these settings at any time.
                </p>
              </div>
            </div>
          </GlassCard>
        </SectionReveal>
      </div>
    </div>
  );
};

export default NotificationPreferencesPage;
